import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Typography,
  Divider,
  Space,
  Form,
  Input,
  Tag,
  Timeline,
  Modal,
  message,
  Alert,
  Spin,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { formatCurrency } from "../../utils/Utils";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";

const { Title, Text } = Typography;
const { TextArea } = Input;

// API Response interface
interface RABillLineAPI {
  raBillLineId: number;
  workOrderLineId: number;
  taskTitle: string;
  contractQuantity: number;
  totalExecutedQty: number;
  totalBilledQty: number;
  billedQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
  workCategoryName: string;
  uomName: string;
}

// Mapped interface for table display
interface RABillLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQuantity: number;
  previousBilledQuantity: number;
  availableQuantity: number;
  thisBillQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
  workCategory: string;
}

interface RABillData {
  raBillLines: RABillLineAPI[];
  payments: any[];
  dms: any[];
  raBillId: number;
  raBillSequenceId: string;
  workOrderId: number;
  workOrderNumber: string;
  contractorId: number;
  contractorName: string;
  siteId: number;
  raPeriodFrom: string;
  raPeriodTo: string;
  placeOfSupply: string;
  contractorGstin: string;
  companyGstin: string;
  taxRegime: string;
  // Additional fields can be added as needed
}

const RABillDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "view"; // 'view', 'approval'

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [raBill, setRABill] = useState<any>(null);
  const [raBillLines, setRABillLines] = useState<RABillLine[]>([]);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return">("approve");

  useEffect(() => {
    if (id) {
      loadRABillDetails();
    }
  }, [id, mode]);

  const loadRABillDetails = async () => {
    if (!id) {
      message.error("RA Bill ID is required");
      return;
    }

    setLoading(true);
    try {
      console.log("Loading RA Bill details for ID:", id);

      // Call the actual API
      const response: any = await RABillApis.getRABillById(id);

      if (response && response.status && response.data) {
        const apiData: RABillData = response.data;
        console.log("Loaded RA Bill data:", apiData);

        // Map API data to component state
        const mappedRABill = {
          id: apiData.raBillId,
          raBillNumber: apiData.raBillSequenceId,
          workOrderNumber: apiData.workOrderNumber,
          contractor: apiData.contractorName,
          contractorId: apiData.contractorId,
          siteId: apiData.siteId,
          site: `Site ${apiData.siteId}`, // You may want to fetch site name separately
          status: "Submitted", // Default status, you may need to get this from API
          raPeriod: {
            from: apiData.raPeriodFrom,
            to: apiData.raPeriodTo,
          },
          placeOfSupply: apiData.placeOfSupply,
          contractorGstin: apiData.contractorGstin,
          companyGstin: apiData.companyGstin,
          taxRegime: apiData.taxRegime,

          // Calculate commercial summary from lines data
          woTotalValue: apiData.raBillLines.reduce(
            (sum, line) => sum + line.contractQuantity * line.rate,
            0
          ),
          cumulativeWorkValue: apiData.raBillLines.reduce(
            (sum, line) => sum + line.lineTotal,
            0
          ),
          currentBillValue: apiData.raBillLines.reduce(
            (sum, line) => sum + line.lineTotal,
            0
          ),
          previousRABillsValue: apiData.raBillLines.reduce(
            (sum, line) =>
              sum + (line.totalBilledQty - line.billedQuantity) * line.rate,
            0
          ),
          cumulativeWorkDone: 85, // You may need to calculate this or get from API
          balanceWorksForRABill: 0, // Calculate based on total vs executed

          // Payment information (if available in API)
          paymentStatus: "Pending",
          paidAmount: 0,
          lastPaymentDate: null,
          paymentMode: null,

          // Audit trail (you may need separate API calls for these)
          createdBy: "System",
          createdOn: new Date().toISOString(),
          submittedBy: null,
          submittedOn: null,
          approvedBy: null,
          approvedOn: null,
        };

        setRABill(mappedRABill);

        // Map RA Bill lines to match the table structure
        const mappedLines = apiData.raBillLines.map((line, index) => ({
          id: line.raBillLineId.toString(),
          workLineNumber: `WL-${line.workOrderLineId}`,
          workLineTitle: line.taskTitle,
          description: line.taskTitle, // Use taskTitle as description if no separate description
          uom: line.uomName,
          contractQuantity: line.contractQuantity,
          cumulativeApprovedQuantity: line.totalExecutedQty,
          previousBilledQuantity: line.totalBilledQty - line.billedQuantity,
          availableQuantity:
            line.totalExecutedQty - (line.totalBilledQty - line.billedQuantity),
          thisBillQuantity: line.billedQuantity,
          rate: line.rate,
          lineSubtotal: line.lineSubtotal,
          gstPercentage: line.gstPercentage,
          gstValue: line.gstValue,
          lineTotal: line.lineTotal,
          workCategory: line.workCategoryName,
        }));

        setRABillLines(mappedLines);

        // Calculate totals from the lines data
        const subtotalExclTax = apiData.raBillLines.reduce(
          (sum, line) => sum + line.lineSubtotal,
          0
        );
        const gstTotal = apiData.raBillLines.reduce(
          (sum, line) => sum + line.gstValue,
          0
        );
        const grossTotal = apiData.raBillLines.reduce(
          (sum, line) => sum + line.lineTotal,
          0
        );

        const calculatedTotals = {
          id: `totals-${apiData.raBillId}`,
          raBillId: apiData.raBillId.toString(),
          subtotalExclTax: subtotalExclTax,
          gstTotal: gstTotal,
          grossTotal: grossTotal,
          totalDeductions: 0, // You may need to calculate deductions from API
          netPayable: grossTotal, // Adjust based on deductions
        };

        setTotals(calculatedTotals);

        // Set empty deductions for now (you may need separate API call)
        setDeductions([]); // apiData.deductions || []

        message.success("RA Bill details loaded successfully");
      } else {
        throw new Error(response?.message || "Failed to fetch RA Bill details");
      }
    } catch (error: any) {
      console.error("Error loading RA Bill details:", error);
      message.error(
        error?.response?.data?.message || "Failed to load RA Bill details"
      );

      // Navigate back if RA Bill not found
      if (error?.response?.status === 404) {
        setTimeout(() => {
          handleBackNavigation();
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "default",
      Submitted: "processing",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Posted: "purple",
      Paid: "green",
      Returned: "error",
    };
    return statusColors[status] || "default";
  };

  const handleBackNavigation = () => {
    const backRoutes: { [key: string]: string } = {
      approval: "ra-bills-pending-approvals",
      view: "ra-bills-view-approved",
    };

    navigate(-1);
  };

  const openActionModal = (type: "approve" | "return") => {
    if (type === "approve") {
      // For approve, show direct confirmation without modal
      handleApproveConfirmation();
    } else {
      // For reject, show modal with comments
      setActionType(type);
      setActionModalVisible(true);
      form.resetFields();
    }
  };

  const handleApproveConfirmation = () => {
    Swal.fire({
      title: "Approve RA Bill?",
      text: `Are you sure you want to approve RA Bill ${raBill?.raBillNumber}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await handleApprove();
      }
    });
  };

  const handleApprove = async () => {
    try {
      setLoading(true);

      const payload = {
        raBillId: id,
        // No comments required for approval
      };

      const response: any = await RABillApis.approveRABill({ raBill: payload });

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "RA Bill Approved!",
          text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          handleBackNavigation();
        });
      } else {
        throw new Error(response?.message || "Failed to approve RA Bill");
      }
    } catch (error: any) {
      console.error("Error approving RA Bill:", error);
      message.error(
        error?.response?.data?.message || "Failed to approve RA Bill"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleActionSubmit = async (values: any) => {
    try {
      setLoading(true);

      const payload = {
        raBillId: id,
        rejectionReason: values.comments || "",
        // Add any other required fields
      };

      let response: any;
      if (actionType === "approve") {
        // Call approve API
        response = await RABillApis.approveRABill(payload);

        if (response && response.status) {
          Swal.fire({
            icon: "success",
            title: "RA Bill Approved!",
            text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
            confirmButtonText: "OK",
          }).then(() => {
            handleBackNavigation();
          });
        } else {
          throw new Error(response?.message || "Failed to approve RA Bill");
        }
      } else {
        // Call reject API
        response = await RABillApis.rejectRABill({ raBill: payload });

        if (response && response.status) {
          Swal.fire({
            icon: "info",
            title: "RA Bill Rejected",
            text: `RA Bill ${raBill?.raBillNumber} has been rejected and returned to contractor`,
            confirmButtonText: "OK",
          }).then(() => {
            handleBackNavigation();
          });
        } else {
          throw new Error(response?.message || "Failed to reject RA Bill");
        }
      }

      setActionModalVisible(false);
    } catch (error: any) {
      console.error(`Error ${actionType}ing RA Bill:`, error);
      message.error(
        error?.response?.data?.message || `Failed to ${actionType} RA Bill`
      );
    } finally {
      setLoading(false);
    }
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<RABillLine> = useMemo(
    () => [
      {
        title: "Work Line #",
        dataIndex: "workLineNumber",
        key: "workLineNumber",
        width: 120,
        fixed: "left",
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        key: "description",
        width: 200,
        render: (_, record) => (
          <div>
            <div className="fw-bold">{record.workLineTitle}</div>
            <div className="text-muted small">{record.description}</div>
          </div>
        ),
      },
      {
        title: "UOM",
        dataIndex: "uom",
        key: "uom",
        width: 80,
        align: "center",
      },
      {
        title: "Contract Qty",
        dataIndex: "contractQuantity",
        key: "contractQuantity",
        width: 120,
        align: "right",
        render: (qty) => qty.toLocaleString(),
      },
      {
        title: "Cum Approved Qty",
        dataIndex: "cumulativeApprovedQuantity",
        key: "cumulativeApprovedQuantity",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text type="success" strong>
            {qty.toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Prev Billed Qty",
        dataIndex: "previousBilledQuantity",
        key: "previousBilledQuantity",
        width: 120,
        align: "right",
        render: (qty) => <Text type="secondary">{qty.toLocaleString()}</Text>,
      },
      {
        title: "Available to Bill",
        dataIndex: "availableQuantity",
        key: "availableQuantity",
        width: 120,
        align: "right",
        render: (qty) => (
          <Text strong style={{ color: "#1890ff" }}>
            {qty.toLocaleString()}
          </Text>
        ),
      },
      {
        title: mode === "approval" ? "Billed Qty (Submitted)" : "Billed Qty",
        dataIndex: "thisBillQuantity",
        key: "thisBillQuantity",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text
            strong
            style={{ color: mode === "approval" ? "#1890ff" : "#52c41a" }}
          >
            {qty.toLocaleString()}
          </Text>
        ),
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        align: "right",
        render: (rate) => `₹${rate.toLocaleString()}`,
      },
      {
        title: "Line Subtotal",
        dataIndex: "lineSubtotal",
        key: "lineSubtotal",
        width: 130,
        align: "right",
        render: (amount) => <Text strong>{formatCurrency(amount, "INR")}</Text>,
      },
      {
        title: "GST %",
        dataIndex: "gstPercentage",
        key: "gstPercentage",
        width: 80,
        align: "center",
        render: (pct) => `${pct}%`,
      },
      {
        title: "GST Value",
        dataIndex: "gstValue",
        key: "gstValue",
        width: 120,
        align: "right",
        render: (amount) => (
          <Text type="warning">{formatCurrency(amount, "INR")}</Text>
        ),
      },
      {
        title: "Line Total",
        dataIndex: "lineTotal",
        key: "lineTotal",
        width: 130,
        align: "right",
        render: (amount) => (
          <Text strong style={{ color: "#52c41a" }}>
            {formatCurrency(amount, "INR")}
          </Text>
        ),
      },
    ],
    [mode]
  );

  if (loading && !raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Spin size="large" />
          <div className="mt-3">Loading RA Bill details...</div>
        </div>
      </Content>
    );
  }

  if (!raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Title level={4}>RA Bill not found</Title>
          <Button onClick={handleBackNavigation}>Go Back</Button>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Head title={`RA Bill Details - ${raBill.raBillNumber}`} />

      <div className="nk-content-inner">
        <div className="nk-content-body">
          {/* Header */}
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <Title level={3} className="nk-block-title">
                  {mode === "approval" ? "Review RA Bill" : "RA Bill Details"} -{" "}
                  {raBill.raBillNumber}
                </Title>
                <div className="nk-block-des text-soft">
                  <Space>
                    <span>WO: {raBill.workOrderNumber}</span>
                    <Divider type="vertical" />
                    <span>Contractor: {raBill.contractor}</span>
                    <Divider type="vertical" />
                    <Tag color={getStatusColor(raBill.status)}>
                      {raBill.status && raBill.status == "Submitted"
                        ? "Pending Approval"
                        : raBill.status}
                    </Tag>
                  </Space>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Space>
                  {/* <Button icon={<EyeOutlined />}>Preview PDF</Button>
                  <Button icon={<DownloadOutlined />}>Export</Button> */}
                  {mode === "approval" && (
                    <>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => openActionModal("approve")}
                      >
                        Approve
                      </Button>
                      <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => openActionModal("return")}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBackNavigation}
                    style={{ marginRight: 16 }}
                  >
                    Back
                  </Button>
                </Space>
              </div>
            </div>
          </div>

          {/* Header Summary */}
          <Card title="RA Bill Header Details" className="mb-4">
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    RA Bill Number:
                  </span>
                  <span className="">{raBill.raBillNumber}</span>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    Work Order:
                  </span>
                  <span className="">{raBill.workOrderNumber}</span>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    Contractor:
                  </span>
                  <span className="">{raBill.contractor}</span>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    RA Period:
                  </span>
                  <span className="">
                    {dayjs(raBill.raPeriod.from).format("DD-MMM")} to{" "}
                    {dayjs(raBill.raPeriod.to).format("DD-MMM-YYYY")}
                  </span>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    Site:
                  </span>
                  <span className="">{raBill.site}</span>
                </div>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <div className="d-flex align-items-center">
                  <span className="text-muted" style={{ minWidth: "120px" }}>
                    Status:
                  </span>
                  <Tag color={getStatusColor(raBill.status)}>
                    {raBill.status && raBill.status == "Submitted"
                      ? "Pending Approval"
                      : raBill.status}
                  </Tag>
                </div>
              </Col>
            </Row>

            {/* Commercial Summary */}
            <Divider className="mt-3">Commercial Summary</Divider>
            <Row gutter={[24, 16]}>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">WO Total Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.woTotalValue, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Done</div>
                  <div className="fw-bold h5 mb-0 text-success">
                    {raBill.cumulativeWorkDone}%
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.cumulativeWorkValue, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Previous RA Bills</div>
                  <div className="fw-bold h5 mb-0 text-info">
                    {formatCurrency(raBill.previousRABillsValue, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Current Bill Value</div>
                  <div className="fw-bold h5 mb-0 text-primary">
                    {formatCurrency(raBill.currentBillValue, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Balance Works</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.balanceWorksForRABill, "INR")}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Work Lines */}
          <Card title="Work Lines Details" className="mb-4">
            <Table
              columns={workLinesColumns}
              dataSource={raBillLines}
              rowKey="id"
              scroll={{ x: 1600 }}
              pagination={false}
              size="small"
            />
          </Card>

          {/* Deductions */}
          <Card title="Deductions & Recoveries" className="mb-4">
            <Row gutter={[24, 16]}>
              {deductions.map((deduction) => (
                <Col xs={24} lg={6} key={deduction.id}>
                  <div className="border rounded p-3">
                    <div className="fw-bold mb-2">
                      {deduction.type}
                      {deduction.percentage && (
                        <span className="text-muted small ms-2">
                          @ {deduction.percentage}%
                        </span>
                      )}
                    </div>
                    <div className="fw-bold text-danger">
                      {formatCurrency(deduction.amount, "INR")}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Totals */}
          {totals && (
            <Card title="Bill Totals" className="mb-4">
              <Row gutter={[24, 16]} className="text-center">
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Subtotal (excl. tax)</div>
                    <div className="fw-bold h5 mb-0 mt-3">
                      {formatCurrency(totals.subtotalExclTax, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">GST Total</div>
                    <div className="fw-bold h5 mb-0 text-warning mt-3">
                      {formatCurrency(totals.gstTotal, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Gross Total</div>
                    <div className="fw-bold h5 mb-0 mt-3">
                      {formatCurrency(totals.grossTotal, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Total Deductions</div>
                    <div className="fw-bold h5 mb-0 text-danger mt-3">
                      -{formatCurrency(totals.totalDeductions, "INR")}
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider />
              <div className="">
                <div
                  className="fw-bold text-success"
                  style={{ fontSize: "20px" }}
                >
                  Net Payable: {formatCurrency(totals.netPayable, "INR")}
                </div>
              </div>
            </Card>
          )}

          {/* Payment Information (for approved bills) */}
          {mode === "view" && raBill.status !== "Draft" && (
            <Card title="Payment Information" className="mb-4">
              <Row gutter={[24, 16]}>
                <Col xs={24} lg={12}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Payment Status</div>
                    <Tag
                      color={
                        raBill.paymentStatus === "Paid"
                          ? "success"
                          : raBill.paymentStatus === "Partially Paid"
                          ? "warning"
                          : "error"
                      }
                    >
                      {raBill.paymentStatus}
                    </Tag>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Amount Paid</div>
                    <div className="fw-bold h5 mb-0 text-success">
                      {formatCurrency(raBill.paidAmount, "INR")}
                    </div>
                  </div>
                </Col>
                {raBill.lastPaymentDate && (
                  <>
                    <Col xs={24} lg={12}>
                      <div className="border rounded p-3">
                        <div className="text-muted small">
                          Last Payment Date
                        </div>
                        <div className="fw-bold h6 mb-0">
                          {dayjs(raBill.lastPaymentDate).format("DD-MMM-YYYY")}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div className="border rounded p-3">
                        <div className="text-muted small">Payment Mode</div>
                        <div className="fw-bold h6 mb-0">
                          {raBill.paymentMode}
                        </div>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          )}

          {/* Audit Trail */}
          {/* <Card title="Audit Trail">
            <Timeline>
              <Timeline.Item
                dot={<UserOutlined className="text-primary" />}
                color="blue"
              >
                <div>
                  <strong>Created</strong> by {raBill.createdByName}
                  <div className="text-muted small">
                    {dayjs(raBill.createdAt).format("DD-MMM-YYYY HH:mm")}
                  </div>
                </div>
              </Timeline.Item>

              {raBill.submittedOn && (
                <Timeline.Item
                  dot={<CalendarOutlined className="text-warning" />}
                  color="orange"
                >
                  <div>
                    <strong>Submitted</strong> by {raBill.submittedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.submittedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}

              {raBill.approvedOn && (
                <Timeline.Item
                  dot={<CheckCircleOutlined className="text-success" />}
                  color="green"
                >
                  <div>
                    <strong>Approved</strong> by {raBill.approvedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.approvedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}
            </Timeline>
          </Card> */}
        </div>
      </div>

      {/* Action Modal */}
      <Modal
        title={actionType === "approve" ? "Approve RA Bill" : "Reject RA Bill"}
        open={actionModalVisible}
        onCancel={() => setActionModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleActionSubmit} layout="vertical">
          <div className="mb-3">
            {/* <Alert
              type={actionType === "approve" ? "success" : "warning"}
              message={
                actionType === "approve"
                  ? `You are about to approve RA Bill ${raBill.raBillNumber}`
                  : `You are about to return RA Bill ${raBill.raBillNumber} to contractor`
              }
              description={
                actionType === "approve"
                  ? "Once approved, this RA Bill will move to the next stage in the workflow."
                  : "Please provide comments explaining why this RA Bill is being returned."
              }
              showIcon
            /> */}
          </div>

          <Form.Item
            label="Comments"
            name="comments"
            rules={
              actionType === "return"
                ? [
                    {
                      required: true,
                      message: "Comments are required when rejecting RA Bill",
                    },
                  ]
                : []
            }
          >
            <TextArea
              rows={4}
              placeholder={`Enter your comments for ${
                actionType === "approve" ? "approval" : "rejection"
              }...`}
            />
          </Form.Item>

          <div className="text-end">
            <Space>
              <Button onClick={() => setActionModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                danger={actionType === "return"}
              >
                {actionType === "approve"
                  ? "Approve RA Bill"
                  : "Reject RA Bill"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </Content>
  );
};

export default RABillDetails;
